import {Editor, useEditor} from '@tiptap/react';
import React, {useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {IElement} from '../../../incase-domain/elements';
import {setEditorAction, setEditorMode} from '../../../store/actions/tiptapEditor.actions';
import {EditorModeEnum} from '../../../store/actions/types/tiptapEditor.types';
import {AppState} from '../../../store/reducer';
import {debounceChangeHandlerSelector, editorModeSelector} from '../../../store/selectors/tiptapEditor.selectors';
import {getElementExtensions} from './extensions/elementExtensions';

interface IGetEditorProps {
    id: string;
    element: IElement;
}

export const useGetEditor = ({
    id,
    element,
}: IGetEditorProps): {
    editor: Editor | null;
    editorMode: EditorModeEnum | null;
    handleDoubleClick: (e: React.MouseEvent) => void;
    handleBlur: () => void;
    handleClick: () => void;
} => {
    const dispatch = useDispatch();
    const debounceChangeHandler = useSelector((state: AppState) => debounceChangeHandlerSelector(state, id));
    const editorMode = useSelector(editorModeSelector);

    function removeFontSizes(cssValue: string): string {
        return cssValue.replace(/font-size\s*:\s*\d*\.?\d+(px|rem|pt|%)(;)?/gi, '');
    }

    const editor = useEditor({
        extensions: getElementExtensions(element),
        content: element.text,
        onUpdate: ({editor}) => {
            const updatedContent = editor.getHTML();
            const sanitizedContent = removeFontSizes(updatedContent);
            if (debounceChangeHandler) debounceChangeHandler(sanitizedContent);
        },
        onBlur: () => {
            dispatch(setEditorMode(null));
        },
        onDestroy: () => {
            dispatch(setEditorMode(null));
        },
    });

    useEffect(() => {
        if (editor) {
            dispatch(setEditorAction(element.id, editor));
            editor.setOptions({
                editable: editorMode === EditorModeEnum.EDIT,
            });
        }
    }, [dispatch, editor, editorMode, element.id]);

    const handleDoubleClick = useCallback(
        (e: React.MouseEvent) => {
            if (!editor) return;
            editor.setOptions({editable: true});
            dispatch(setEditorMode(EditorModeEnum.EDIT));

            if (editorMode !== EditorModeEnum.EDIT) {
                const coords = editor.view.posAtCoords({left: e.clientX, top: e.clientY});
                if (coords) {
                    editor.commands.setTextSelection(coords.pos);
                }
            }

            editor.commands.focus();
        },
        [dispatch, editor, editorMode]
    );

    const handleBlur = useCallback(() => {
        editor?.commands.blur();
        dispatch(setEditorMode(null));
    }, [dispatch, editor?.commands]);

    const handleClick = useCallback(() => {
        if (editorMode !== EditorModeEnum.EDIT) {
            dispatch(setEditorMode(EditorModeEnum.SELECT));
        }
    }, [dispatch, editorMode]);

    return {
        editor,
        editorMode,
        handleDoubleClick,
        handleBlur,
        handleClick,
    };
};
